import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { api, CoachMessage } from '../lib/api';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<CoachMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Allow external components to open the chat via custom event
  useEffect(() => {
    const handleOpen = () => setIsOpen(true);
    window.addEventListener('open-coach-chat', handleOpen);
    return () => window.removeEventListener('open-coach-chat', handleOpen);
  }, []);

  // Poll for unread count
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const data = await api.getUnreadCoachCount();
        setUnread(data.unread_count);
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load messages when chat opens
  useEffect(() => {
    if (!isOpen) return;
    const loadMessages = async () => {
      try {
        const data = await api.getCoachMessages();
        setMessages(data.messages);
        await api.markCoachMessagesRead();
        setUnread(0);
      } catch (e) { console.error(e); }
    };
    loadMessages();
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const extractPhaseSlug = () => {
    const match = location.pathname.match(/\/phases\/([^/]+)/);
    return match ? match[1] : undefined;
  };

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const result = await api.sendCoachMessage(input.trim(), {
        page_url: location.pathname,
        phase_slug: extractPhaseSlug(),
      });
      setMessages(prev => [...prev, result.message]);
      setInput('');
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 60000);
    if (diff < 1) return 'now';
    if (diff < 60) return `${diff}m`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h`;
    return d.toLocaleDateString();
  };

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-6 w-[360px] h-[480px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden z-50 animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-charcoal-700 to-charcoal-800 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-copper-400" />
              <div>
                <h3 className="font-semibold text-sm">Ask My Coach</h3>
                <p className="text-xs text-copper-300">Get help with any step</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-charcoal-300 hover:text-white transition-colors">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <ChatBubbleLeftRightIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No messages yet.</p>
                <p className="text-xs mt-1">Ask your coach anything!</p>
              </div>
            )}
            {messages.map(m => (
              <div key={m.id} className={`flex ${m.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${
                  m.sender_type === 'client'
                    ? 'bg-copper-500 text-white rounded-br-md'
                    : 'bg-white text-charcoal-800 rounded-bl-md shadow-sm border border-gray-100'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                  <p className={`text-[10px] mt-0.5 ${m.sender_type === 'client' ? 'text-copper-200' : 'text-gray-400'}`}>
                    {m.created_at ? formatTime(m.created_at) : ''}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-gray-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Ask your coach..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-transparent"
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="px-3 py-2 bg-copper-500 text-white rounded-xl hover:bg-copper-600 transition-colors disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-copper-500 text-white rounded-full shadow-lg shadow-copper-500/30 hover:bg-copper-600 hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center z-50"
      >
        {isOpen ? (
          <XMarkIcon className="h-6 w-6" />
        ) : (
          <>
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {unread}
              </span>
            )}
          </>
        )}
      </button>
    </>
  );
}
